<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from "vue";
import Icon from "./Icon.vue";
import { api, ApiError, emptyBilling, type AuthConfig, type User } from "../api";
import Logo from "./Logo.vue";
import BillingFields from "./BillingFields.vue";

const emit = defineEmits<{ authed: [User] }>();

const tab = ref<"login" | "register">("login");
const busy = ref(false);
const err = ref("");
const info = ref("");
const cfg = ref<AuthConfig>({ captcha: "off", captchaSiteKey: null, openRegistration: false, mailEnabled: false, siteUrl: null });
const site = () => cfg.value.siteUrl ?? "https://cleanops.pl";
const acceptTerms = ref(false);

// --- Wbudowana captcha obrazkowa ---
// Serwer generuje SVG z kodem; odsyłamy id wyzwania i to, co użytkownik wpisał.
// Każde wyzwanie jest jednorazowe, więc po każdej próbie (udanej czy nie)
// pobieramy nowe.
const builtin = ref<{ id: string; svg: string } | null>(null);
const builtinAnswer = ref("");
async function loadBuiltin() {
  if (cfg.value.captcha !== "builtin") return;
  builtinAnswer.value = "";
  try {
    builtin.value = await api.get<{ id: string; svg: string }>("/api/auth/captcha");
  } catch (e) {
    err.value = (e as Error).message;
  }
}

const email = ref("");
const pass = ref("");
// Logowanie na nieaktywowane konto: pokazujemy przycisk „wyślij link ponownie".
const unverified = ref(false);

const rgCode = ref("");
const rgEmail = ref("");
const rgPass = ref("");
const rgPass2 = ref("");
// Dane do faktury — wymagane przy rejestracji otwartej (naprawa jest płatna).
const rgBilling = ref(emptyBilling());

// --- Captcha (Cloudflare Turnstile) ---
//
// Skrypt ładujemy tylko, gdy panel ma skonfigurowany klucz — bez niego nie ma
// ani widżetu, ani żądań do Cloudflare. Token jest jednorazowy: po każdym
// wysłaniu formularza (udanym czy nie) widżet resetujemy, inaczej drugi klik
// poleci ze zużytym tokenem i dostanie „captcha nie powiodła się" bez winy
// użytkownika.
declare global {
  interface Window {
    turnstile?: {
      render(el: HTMLElement, opts: Record<string, unknown>): string;
      reset(id?: string): void;
      remove(id: string): void;
    };
  }
}
const captchaEl = ref<HTMLElement | null>(null);
const captchaToken = ref("");
let widgetId: string | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Nie udało się załadować captchy."));
    document.head.appendChild(s);
  });
}

async function mountCaptcha() {
  if (cfg.value.captcha === "builtin") return loadBuiltin();
  if (cfg.value.captcha !== "turnstile" || !cfg.value.captchaSiteKey) return;
  try {
    await loadTurnstile();
    await nextTick();
    if (!captchaEl.value || !window.turnstile) return;
    if (widgetId) window.turnstile.remove(widgetId);
    captchaToken.value = "";
    widgetId = window.turnstile.render(captchaEl.value, {
      sitekey: cfg.value.captchaSiteKey,
      language: "pl",
      callback: (t: string) => (captchaToken.value = t),
      "expired-callback": () => (captchaToken.value = ""),
      "error-callback": () => (captchaToken.value = ""),
    });
  } catch (e) {
    err.value = (e as Error).message;
  }
}

function resetCaptcha() {
  captchaToken.value = "";
  if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
  if (cfg.value.captcha === "builtin") void loadBuiltin();
}

/** Pola captchy dołączane do każdego żądania auth. */
function captchaFields() {
  if (cfg.value.captcha === "turnstile") return { captchaToken: captchaToken.value || undefined };
  if (cfg.value.captcha === "builtin") return { captchaId: builtin.value?.id, captchaAnswer: builtinAnswer.value };
  return {};
}

onMounted(async () => {
  // Odwiedziny ekranu logowania — statystyka dla admina („czy ktoś w ogóle wchodzi?").
  void api.post("/api/track", { event: "login_page" }).catch(() => {});
  try {
    cfg.value = await api.get<AuthConfig>("/api/auth/config");
  } catch {
    /* panel bez konfiguracji działa jak dotąd */
  }
  await mountCaptcha();
});
// Jeden kontener captchy na obie zakładki — po przełączeniu renderujemy od nowa.
watch(tab, () => {
  err.value = "";
  info.value = "";
  unverified.value = false;
  void mountCaptcha();
});

function needCaptcha(): boolean {
  if (cfg.value.captcha === "turnstile" && !captchaToken.value) {
    err.value = "Potwierdź, że nie jesteś robotem.";
    return true;
  }
  if (cfg.value.captcha === "builtin" && builtinAnswer.value.trim().length < 3) {
    err.value = "Przepisz kod z obrazka.";
    return true;
  }
  return false;
}

async function doLogin() {
  err.value = "";
  info.value = "";
  unverified.value = false;
  if (needCaptcha()) return;
  busy.value = true;
  try {
    const d = await api.post<{ user: User }>("/api/auth/login", {
      email: email.value,
      password: pass.value,
      ...captchaFields(),
    });
    emit("authed", d.user);
  } catch (e) {
    err.value = (e as Error).message;
    if (e instanceof ApiError && e.code === "unverified") unverified.value = true;
  } finally {
    busy.value = false;
    resetCaptcha();
  }
}

async function resend() {
  err.value = "";
  busy.value = true;
  try {
    const d = await api.post<{ message: string }>("/api/auth/resend", {
      email: email.value,
      ...captchaFields(),
    });
    info.value = d.message;
    unverified.value = false;
  } catch (e) {
    err.value = (e as Error).message;
  } finally {
    busy.value = false;
    resetCaptcha();
  }
}

async function doRegister() {
  err.value = "";
  info.value = "";
  // Sprawdzamy po stronie klienta tylko to, czego serwer sprawdzić nie może:
  // czy człowiek dwa razy wpisał to samo. Długość hasła waliduje serwer i to
  // on jest źródłem prawdy — powielanie reguły tutaj znaczyłoby, że przy
  // zmianie polityki trzeba pamiętać o dwóch miejscach.
  if (rgPass.value !== rgPass2.value) {
    err.value = "Hasła nie są takie same.";
    return;
  }
  if (!acceptTerms.value) {
    err.value = "Zaakceptuj regulamin i politykę prywatności.";
    return;
  }
  if (needCaptcha()) return;
  busy.value = true;
  try {
    const d = await api.post<{ user?: User; pending?: boolean; message?: string }>("/api/auth/register", {
      code: rgCode.value || undefined,
      email: rgEmail.value,
      password: rgPass.value,
      billing: rgBilling.value,
      acceptTerms: acceptTerms.value,
      ...captchaFields(),
    });
    if (d.pending) {
      // Konto czeka na link z maila — nie ma jeszcze sesji.
      info.value = d.message ?? "Sprawdź skrzynkę i kliknij w link aktywacyjny.";
      tab.value = "login";
      email.value = rgEmail.value;
      await nextTick();
      info.value = d.message ?? "Sprawdź skrzynkę i kliknij w link aktywacyjny.";
      return;
    }
    if (d.user) emit("authed", d.user);
  } catch (e) {
    err.value = (e as Error).message;
  } finally {
    busy.value = false;
    resetCaptcha();
  }
}
</script>

<template>
  <div class="auth">
    <div class="brand"><Logo :size="44" /></div>
    <p class="sub">Naprawa zainfekowanych stron — bezpiecznie i odwracalnie</p>

    <div class="tabs">
      <button type="button" :class="{ on: tab === 'login' }" @click="tab = 'login'">
        Logowanie
      </button>
      <button type="button" :class="{ on: tab === 'register' }" @click="tab = 'register'">
        {{ cfg.openRegistration ? "Załóż konto" : "Mam zaproszenie" }}
      </button>
    </div>

    <div class="card">
      <div class="body">
        <form v-if="tab === 'login'" @submit.prevent="doLogin">
          <div class="field">
            <label for="li-email">E-mail</label>
            <input id="li-email" v-model="email" type="email" autocomplete="username" required>
          </div>
          <div class="field">
            <label for="li-pass">Hasło</label>
            <input id="li-pass" v-model="pass" type="password" autocomplete="current-password" required>
          </div>
          <div v-if="cfg.captcha === 'turnstile'" ref="captchaEl" class="captcha" />
          <div v-else-if="cfg.captcha === 'builtin' && builtin" class="field captchabox">
            <label for="cap-li">Przepisz kod z obrazka</label>
            <div class="caprow">
              <div class="capimg" v-html="builtin.svg" />
              <button type="button" class="link" title="Inny obrazek" @click="loadBuiltin"><Icon name="refresh" /></button>
            </div>
            <input id="cap-li" v-model="builtinAnswer" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="8" placeholder="5 znaków">
          </div>
          <p v-if="info" class="info">{{ info }}</p>
          <p v-if="err" class="err">{{ err }}</p>
          <button v-if="unverified" type="button" class="sec" style="width:100%; margin-bottom:8px" :disabled="busy" @click="resend">
            <Icon name="mail" /> Wyślij link aktywacyjny ponownie
          </button>
          <button type="submit" :disabled="busy" style="width:100%">
            {{ busy ? "Loguję…" : "Zaloguj" }}
          </button>
        </form>

        <form v-else @submit.prevent="doRegister">
          <p v-if="cfg.openRegistration" class="intro">
            Konto jest bezpłatne. Płacisz tylko za wykonane naprawy, po ich zakończeniu.
          </p>
          <div class="field">
            <label for="rg-email">E-mail</label>
            <input id="rg-email" v-model="rgEmail" type="email" autocomplete="username" required>
            <p v-if="cfg.mailEnabled" class="hint">Na ten adres wyślemy link aktywacyjny.</p>
          </div>
          <div class="field">
            <label for="rg-pass">Hasło</label>
            <input id="rg-pass" v-model="rgPass" type="password" autocomplete="new-password" required>
            <p class="hint">Minimum 12 znaków.</p>
          </div>
          <div class="field">
            <label for="rg-pass2">Powtórz hasło</label>
            <input id="rg-pass2" v-model="rgPass2" type="password" autocomplete="new-password" required>
          </div>

          <fieldset class="bill">
            <legend>Dane do faktury</legend>
            <p class="hint" style="margin-top:0">Naprawa jest płatna i fakturowana po zakończeniu. Sprawdzenie strony nic nie kosztuje.</p>
            <BillingFields v-model="rgBilling" id-prefix="rg" />
          </fieldset>
          <!-- Kod zaproszenia: przy rejestracji otwartej schowany pod linkiem —
               większość klientów go nie ma i nie powinna się zastanawiać, skąd go wziąć. -->
          <details v-if="cfg.openRegistration" class="codebox" :open="!!rgCode || undefined">
            <summary>Mam kod zaproszenia</summary>
            <div class="field">
              <label for="rg-code">Kod zaproszenia</label>
              <input id="rg-code" v-model="rgCode" type="text">
            </div>
          </details>
          <div v-else class="field">
            <label for="rg-code">Kod zaproszenia</label>
            <input id="rg-code" v-model="rgCode" type="text" required>
            <p class="hint">Kod dostajesz od administratora. Ważny 7 dni.</p>
          </div>
          <div class="field check terms">
            <input id="rg-terms" v-model="acceptTerms" type="checkbox" required>
            <label for="rg-terms">Akceptuję <a :href="site() + '/regulamin/'" target="_blank" rel="noopener">regulamin</a> i <a :href="site() + '/polityka-prywatnosci/'" target="_blank" rel="noopener">politykę prywatności</a>.</label>
          </div>
          <div v-if="cfg.captcha === 'turnstile'" ref="captchaEl" class="captcha" />
          <div v-else-if="cfg.captcha === 'builtin' && builtin" class="field captchabox">
            <label for="cap-rg">Przepisz kod z obrazka</label>
            <div class="caprow">
              <div class="capimg" v-html="builtin.svg" />
              <button type="button" class="link" title="Inny obrazek" @click="loadBuiltin"><Icon name="refresh" /></button>
            </div>
            <input id="cap-rg" v-model="builtinAnswer" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="8" placeholder="5 znaków">
          </div>
          <p v-if="err" class="err">{{ err }}</p>
          <button type="submit" :disabled="busy" style="width:100%">
            {{ busy ? "Zakładam…" : "Załóż konto" }}
          </button>
        </form>
      </div>
    </div>
    <p class="trustline">
      <Icon name="lock" /> Dane dostępowe szyfrowane AES-256-GCM, serwery w Unii Europejskiej.
      <a :href="site() + '/bezpieczenstwo/'" target="_blank" rel="noopener">Jak chronimy dane</a> ·
      <a :href="site() + '/regulamin/'" target="_blank" rel="noopener">Regulamin</a> ·
      <a :href="site() + '/polityka-prywatnosci/'" target="_blank" rel="noopener">Prywatność</a>
    </p>
  </div>
</template>

<style scoped>
.trustline { text-align: center; font-size: 12px; color: var(--mut); margin-top: var(--s4); line-height: 1.7; }
.trustline a { color: var(--mut); text-decoration: underline; }
.terms { margin-top: var(--s3); }
.terms label a { color: var(--acc); }
.auth { max-width: 410px; margin: 9vh auto; padding: 0 var(--s4); }
.brand { display: flex; justify-content: center; margin-bottom: var(--s2); }
.sub { text-align: center; color: var(--mut); font-size: 13px; margin: 0 0 var(--s5); }
.tabs {
  display: flex; gap: var(--s1); margin-bottom: var(--s4); background: var(--panel2);
  padding: var(--s1); border-radius: var(--r2); border: 1px solid var(--bd);
}
.tabs button { flex: 1; background: transparent; border: 0; color: var(--mut); font-size: 13px; padding: 8px; }
.tabs button.on { background: var(--panel); color: var(--fg); box-shadow: var(--shadow); }
.card { padding: var(--s5); }
.captcha { margin: var(--s3) 0; min-height: 65px; }
.captchabox { margin-top: var(--s3); }
.caprow { display: flex; align-items: center; gap: var(--s2); margin-bottom: var(--s2); }
.capimg :deep(svg) { display: block; border-radius: var(--r1); border: 1px solid var(--bd2); max-width: 100%; height: auto; }
.intro { font-size: 13px; color: var(--mut); margin: 0 0 var(--s3); }
.bill { margin-top: var(--s3); }
.codebox { margin: var(--s3) 0; }
.codebox > summary { cursor: pointer; font-size: 12px; color: var(--mut); user-select: none; }
.codebox > summary:hover { color: var(--fg); }
.info { font-size: 12px; color: var(--ok); background: var(--ok-dim); border-radius: var(--r1); padding: var(--s2) var(--s3); margin: var(--s2) 0; }
</style>
